<template>
  <div class="admin-owner-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('adminOwner.page.title')" :subtitle="$t('adminOwner.page.subtitle')" />

    <!-- 小区选择 -->
    <div class="community-switch">
      <select-admin-community ref="selectCommunity" @changeCommunity="handleCommunityChange"></select-admin-community>
    </div>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.personRole" clearable class="w-full"
            :placeholder="$t('adminOwner.selectPersonRole')">
            <el-option :label="$t('adminOwner.all')" value=""></el-option>
            <el-option v-for="item in personRoles" :key="item.statusCd" :label="item.name"
              :value="item.statusCd"></el-option>
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.personType" clearable class="w-full"
            :placeholder="$t('adminOwner.selectPersonType')">
            <el-option :label="$t('adminOwner.all')" value=""></el-option>
            <el-option :label="$t('adminOwner.personal')" value="P"></el-option>
            <el-option :label="$t('adminOwner.company')" value="C"></el-option>
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.name" clearable
            :placeholder="$t('adminOwner.inputCustomerName')" @keyup.enter.native="queryOwner"></el-input>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.roomName" clearable
            :placeholder="$t('adminOwner.inputRoomNumber')" @keyup.enter.native="queryOwner"></el-input>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.link" clearable
            :placeholder="$t('adminOwner.inputContact')" @keyup.enter.native="queryOwner"></el-input>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.idCard" clearable
            :placeholder="$t('adminOwner.inputIdCard')" @keyup.enter.native="queryOwner"></el-input>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryOwner">
          {{ $t('adminOwner.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetOwner">
          {{ $t('adminOwner.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="queryOwner">
        <template #left>
          <span class="ds-panel__title">{{ $t('adminOwner.ownerInfo') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="ownerList" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('adminOwner.page.empty')" @retry="queryOwner">
        <el-table-column prop="faceUrl" :label="$t('adminOwner.face')" width="100" align="center">
          <template slot-scope="scope">
            <el-image style="width: 60px; height: 60px;" :src="scope.row.faceUrl || '/img/noPhoto.jpg'"
              :preview-src-list="[scope.row.faceUrl || '/img/noPhoto.jpg']" fit="cover" class="border-radius">
            </el-image>
          </template>
        </el-table-column>

        <el-table-column prop="communityName" :label="$t('adminOwner.communityName')" align="center" min-width="130"
          show-overflow-tooltip></el-table-column>
        <el-table-column prop="name" :label="$t('adminOwner.customerName')" align="center" min-width="110">
          <template slot-scope="scope">
            <div>{{ scope.row.name }}</div>
            <div v-if="scope.row.personType === 'C'" class="ds-cell-sub">{{ scope.row.concactPerson }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="personTypeName" :label="$t('adminOwner.personType')" align="center" width="100"></el-table-column>
        <el-table-column prop="personRoleName" :label="$t('adminOwner.personRole')" align="center" width="110"></el-table-column>
        <el-table-column prop="sex" :label="$t('adminOwner.gender')" align="center" width="80">
          <template slot-scope="scope">
            {{ scope.row.sex === 0 ? $t('adminOwner.male') : $t('adminOwner.female') }}
          </template>
        </el-table-column>
        <el-table-column prop="idCard" :label="$t('adminOwner.idCard')" align="center" min-width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.idCard || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="link" :label="$t('adminOwner.contactPhone')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.link || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="concactLink" :label="$t('adminOwner.backupPhone')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.concactLink || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="address" :label="$t('adminOwner.address')" align="center" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.address || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="roomCount" :label="$t('adminOwner.roomCount')" align="center" width="90">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.roomCount || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="memberCount" :label="$t('adminOwner.memberCount')" align="center" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.memberCount || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="carCount" :label="$t('adminOwner.carCount')" align="center" width="90">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.carCount || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="oweFee" :label="$t('adminOwner.oweFee')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.oweFee) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('adminOwner.operation')" align="center" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="toOwnerDetail(scope.row)">
              {{ $t('adminOwner.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.currentPage" :size="pagination.pageSize"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <view-image ref="viewImage"></view-image>
  </div>
</template>

<script>
import { queryAdminOwners } from '@/api/owner/adminOwnerApi'
import SelectAdminCommunity from '@/components/community/selectAdminCommunity'
import ViewImage from '@/components/owner/viewImage'
import { getDict } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'AdminOwnerList',
  components: {
    SelectAdminCommunity,
    ViewImage,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        personRole: '',
        personType: '',
        name: '',
        roomName: '',
        link: '',
        idCard: '',
        communityId: ''
      },
      ownerList: [],
      personRoles: [],
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  created() {
    this.loadPersonRoles()
    this.queryOwner()
  },
  methods: {
    formatMoney(value) {
      return formatMoney(value)
    },
    async loadPersonRoles() {
      try {
        const data = await getDict('building_owner', 'person_role')
        this.personRoles = [...data]
      } catch (error) {
        console.log(error)
      }
    },
    queryOwner() {
      const params = {
        ...this.searchForm,
        page: this.pagination.currentPage,
        row: this.pagination.pageSize
      }

      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      queryAdminOwners(params).then(res => {
        this.ownerList = res.data
        this.pagination.total = res.total
      }).catch(error => {
        console.log(error)
        this.hasError = true
        this.errorMessage = this.$t('adminOwner.fetchError')
      }).finally(() => {
        this.loading = false
      })
    },
    resetOwner() {
      this.searchForm = {
        personRole: '',
        personType: '',
        name: '',
        roomName: '',
        link: '',
        idCard: '',
        communityId: this.searchForm.communityId
      }
      this.pagination.currentPage = 1
      this.queryOwner()
    },
    handleCommunityChange(community) {
      this.searchForm.communityId = community.communityId
      this.queryOwner()
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.queryOwner()
    },
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.queryOwner()
    },
    toOwnerDetail(owner) {
      this.$router.push(`/views/aCommunity/adminOwnerDetail?ownerId=${owner.ownerId}`)
    },
    viewOwnerFace(url) {
      this.$refs.viewImage.open(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-owner-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 小区切换区 */
.community-switch {
  margin-bottom: var(--ds-spacing-md);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}

/* 单元格次要说明 */
.ds-cell-sub {
  font-size: var(--ds-font-size-sm);
  color: var(--ds-gray-500);
}

.border-radius {
  border-radius: 4px;
}
</style>
